import { PropsWithChildren, Component, ErrorInfo } from 'react'

import { VStack } from 'styled-system/jsx'
import { Button } from 'ui-kit/button'
import { Heading } from 'ui-kit/heading'
import { Text } from 'ui-kit/text'

import { onErrorHandler, ErrorHandlerErrorType, ErrorHandlerDataType } from './onErrorHandler'

// Global handler for app errors
function handleAppError(error: ErrorHandlerErrorType, data?: ErrorHandlerDataType) {
  onErrorHandler(error, data)
}

export function AppErrorHandler({ children }: PropsWithChildren) {
  return <AppErrorBoundary onError={handleAppError}>{children}</AppErrorBoundary>
}

interface AppErrorBoundaryProps extends PropsWithChildren {
  onError: (error: ErrorHandlerErrorType, data?: ErrorHandlerDataType) => void
}

interface AppErrorBoundaryState {
  error: Error | null // The error object to use when showing fallback
  loopError: boolean
  errorTimestamp: number
}

class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props)
    // Initialize state
    this.state = { loopError: false, error: null, errorTimestamp: 0 }
  }

  private isLoopError(error: Error) {
    const sameMessage = !!this.state.error && this.state.error.message === error.message
    const smallTimesptampDiff = Date.now() - this.state.errorTimestamp < 100

    return sameMessage && smallTimesptampDiff
  }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: ErrorHandlerErrorType, { componentStack }: ErrorInfo) {
    this.props.onError(error, { componentStack })

    // Update the state detecting the loop error or not
    this.setState((prevState) => ({
      ...prevState,
      errorTimestamp: Date.now(),
      loopError: this.isLoopError(error),
    }))
  }

  handleReset = () => {
    // Reset all error-related state
    this.setState({ loopError: false, error: null })
  }

  render() {
    if (this.state.loopError && this.state.error) {
      return <InfiniteLoopErrorFallback resetErrorBoundary={this.handleReset} />
    }

    return this.props.children
  }
}

function InfiniteLoopErrorFallback({ resetErrorBoundary }: { resetErrorBoundary: () => void }) {
  return (
    <VStack
      flexDirection='column'
      gap='4'
      width='sm'
      height='screen'
      marginInline='auto'
      alignItems='start'
      justifyContent='center'
      role='alert'
      aria-live='assertive'
    >
      <VStack gap='0' alignItems='start'>
        <Text m='0' color='fg.muted' lineHeight='tight'>
          Something went wrong
        </Text>
        <Heading m='0' size='3xl' as='h1' lineHeight='tight'>
          Infinite error loop detected
        </Heading>
      </VStack>
      <Button colorPalette='primary' variant='solid' onClick={resetErrorBoundary}>
        Try again
      </Button>
    </VStack>
  )
}
